import { memo, useMemo, useCallback, useState, useEffect, useRef } from "react";
import {
  Calendar,
  Building2,
  MapPin,
  Globe,
  CreditCard,
  FileText,
  Briefcase,
} from "lucide-react";

import "./index.css";
import SlidingModal from "../../SlidingModal/slidingModal";
import AddProjectPopup from "../../AddProjectPopup";
import { usePermissions } from "../../../redux/slices/permissionsSlice";
import { FEATURE_CODES } from "../../../util/featureCodes";
const InfoItem = memo(function InfoItem({ icon: Icon, label, value }) {
  return (
    <div className="info-item">
      <div className="info-item-icon-wrapper">
        <Icon size={16} className="info-item-icon" />
      </div>
      <div className="info-item-content">
        <p className="info-item-label mb-1">{label}</p>
        <p className="info-item-value mb-0">{value}</p>
      </div>
    </div>
  );
});

export const ProjectDetailsCard = memo(function ProjectDetailsCard({ project, cloneDefaultValues, timeline , onCloneProject, isUpdating, errorMsg
 }) {
  const { hasPermission } = usePermissions();
  const canCloneProject = hasPermission(FEATURE_CODES.PROJECT_EDIT);
  const [showHistory, setShowHistory] = useState(false);
  const dropdownRef = useRef(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowHistory(false);
      }
    };

    if (showHistory) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHistory]);
    const toggleIsPopupOpen = useCallback(() => {
      setIsPopupOpen(false);
     
    }, []);
      const handleCloneProject =  (projectData) => {
        onCloneProject(projectData);
          setIsPopupOpen(false);
        
     }
   
  const formatDate = useCallback((date) => {
    if (date === "Ongoing" || !date) return date || "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, []);
  const showProjectPopup = useCallback(() => {
      setIsPopupOpen(true);
    }, []);

  const duration = useMemo(() => {
    if (!project?.startDate) return "N/A";
    
    return project.endDate === null
      ? `${formatDate(project.startDate)} → Ongoing`
      : `${formatDate(project.startDate)} → ${formatDate(project.endDate)}`;
  }, [project?.startDate, project?.endDate, formatDate]);

  const clientLocation = useMemo(
    () => `${project?.clientState || "N/A"}, ${project?.clientCountry || "N/A"}`,
    [project?.clientState, project?.clientCountry]
  );

  const badgeClass = useMemo(() => {
  const status = project?.status;

  const statusClassMap = {
    ACTIVE: "bg-success",
    PENDING: "bg-warning",
    CLOSED: "bg-danger",
    PAUSED: "bg-secondary",
  };

  const badgeColorClass = statusClassMap[status] || "bg-danger";

  return `badge ${badgeColorClass} text-uppercase px-3 py-2`;
}, [project?.status]);

  const projectInfo = useMemo(
    () => [
      {
        icon: Calendar,
        label: "Project Duration",
        value: duration,
      },
      { icon: Building2, label: "Client Name", value: project?.clientName || "N/A" },
      { icon: MapPin, label: "Client Location", value: clientLocation },
      { icon: Globe, label: "Client Address", value: project?.clientAddress || "N/A" },
      { icon: CreditCard, label: "Billing Currency", value: project?.billingCurrency || "N/A" },
    ],
    [duration, clientLocation, project]
  );

  return (
    <div className="card shadow-sm p-3 m-4" style={cardStyle}>
      {/* Header */}
      <div className="project-details-header">
        <div className="project-details-header-content">
          <div className="project-icon-wrapper">
            <Briefcase size={24} className="project-icon" />
          </div>
          <div>
            <h4 className="project-title mb-0">{project?.name || "N/A"}</h4>
            <small className="project-subtitle d-block">Project Overview</small>
          </div>
        </div>
       <div className="d-flex gap-2 position-relative" ref={dropdownRef}>
         <button 
className="btn btn-primary btn-sm d-flex align-items-center gap-2 text-uppercase px-1 py-1"           onClick={() => setShowHistory(!showHistory)}
           style={{ border: 'none', cursor: 'pointer' }}
         >
           History
         </button>
         {showHistory && (
           <div className="history-dropdown mt-2">
             {timeline?.slice().reverse().map((item, index) => (
               <div key={index} className="history-item">
                 <span>{item.startDate} - {item.endDate || 'Ongoing'} →</span>
                 <span className={`status ${item.status.toLowerCase()}`}>{item.status}</span>
               </div>
             ))}
           </div>
         )}
        <span className={badgeClass}>{project?.status || "N/A"}</span>
         { canCloneProject && project?.status === "CLOSED" && <button
className="btn btn-primary bg-success btn-sm d-flex align-items-center gap-2 text-uppercase px-1 py-1"
           style={{ border: 'none', cursor: 'pointer' }}
           onClick={() => showProjectPopup()}
         >
           Clone
         </button>}
        
       </div>
      </div>

      {/* Info Grid */}
      <div className="project-info-grid">
        {projectInfo.map((info, index) => (
          <InfoItem
            key={index}
            icon={info.icon}
            label={info.label}
            value={info.value}
          />
        ))}
        {project?.billingCountry?.toLowerCase() === "india" && (
          <InfoItem icon={FileText} label="GST Number" value={project?.gstNumber || "N/A"} />
        )}
      </div>
       <SlidingModal
              modalName={ "Clone Project"}
              closeBtn={toggleIsPopupOpen}
              isModalOpen={isPopupOpen}
            >
              <AddProjectPopup
                popupOpen={isPopupOpen}
                toggleIsPopupOpen={toggleIsPopupOpen}
                closeBtn={toggleIsPopupOpen}
                onSubmitData={ handleCloneProject }
                defaultValues={cloneDefaultValues}
                isEditMode={false}
                isCloneMode={true}
                isLoading={isUpdating}
                errorMsg={errorMsg}
              />
            </SlidingModal>
    </div>
  );
});

const cardStyle = {
  width:"80vw",
  borderRadius: "12px",
};